import React, { useState } from 'react';
import { Intervention } from '../../../types';

interface InterventionCardProps {
  intervention: Intervention;
  onSubmit: (answer: string) => void;
  disabled?: boolean;
}

const ChatIcon = () => (
  <svg 
    className="w-5 h-5 text-neutral-800 dark:text-neutral-200" 
    fill="none" 
    viewBox="0 0 24 24" 
    stroke="currentColor" 
    strokeWidth="1.8"
  >
    <path 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      d="M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.497c1.585-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.12-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" 
    />
  </svg>
);

export const InterventionCard: React.FC<InterventionCardProps> = ({ intervention, onSubmit, disabled = false }) => {
  const [value, setValue] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const title = intervention.context?.intent || "Filter Female Students";

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!value) return;
    
    let finalValue = value;
    if (finalValue.startsWith('_custom:')) {
      finalValue = finalValue.replace('_custom:', '');
    }
    
    setIsSubmitting(true);
    onSubmit(finalValue);
  };

  const renderInput = () => {
    switch (intervention.question_type) {
      case 'SINGLE_SELECT':
      case 'RESOURCE_SELECT':
        return (
          <div className="flex flex-col gap-4 mt-5">
            <p className="text-[14.5px] font-normal text-neutral-900 dark:text-neutral-100">
              1. {intervention.question}
            </p>
            <div className="flex flex-col gap-3.5 pl-1">
              {intervention.options?.map((opt, i) => {
                const isSelected = value === opt;
                return (
                  <label 
                    key={i} 
                    className="flex items-start gap-3 cursor-pointer group select-none"
                  >
                    <input 
                      type="radio" 
                      name="intervention_option" 
                      value={opt}
                      checked={isSelected}
                      onChange={(e) => setValue(e.target.value)}
                      className="sr-only"
                      disabled={disabled || isSubmitting}
                    />
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all mt-0.5 ${
                      isSelected 
                        ? 'border-neutral-700 dark:border-neutral-300' 
                        : 'border-neutral-300 dark:border-neutral-600 group-hover:border-neutral-400 dark:group-hover:border-neutral-500'
                    }`}>
                      {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-neutral-700 dark:bg-neutral-300" />}
                    </div>
                    <span className="text-[14px] font-normal leading-normal text-neutral-800 dark:text-neutral-200">
                      {opt}
                    </span>
                  </label>
                );
              })}
              
              {intervention.allow_custom_input && (
                <label className="flex items-center gap-3 cursor-pointer group select-none">
                  <input 
                    type="radio" 
                    name="intervention_option" 
                    value="_custom"
                    checked={value.startsWith('_custom:')}
                    onChange={() => setValue('_custom:')}
                    className="sr-only"
                    disabled={disabled || isSubmitting}
                  />
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                    value.startsWith('_custom:') 
                      ? 'border-neutral-700 dark:border-neutral-300' 
                      : 'border-neutral-300 dark:border-neutral-600 group-hover:border-neutral-400 dark:group-hover:border-neutral-500'
                  }`}>
                    {value.startsWith('_custom:') && <div className="w-2.5 h-2.5 rounded-full bg-neutral-700 dark:bg-neutral-300" />}
                  </div>
                  <span className="text-[14px] font-normal text-neutral-800 dark:text-neutral-200 shrink-0">Other</span>
                  <input 
                    type="text"
                    placeholder=""
                    value={value.startsWith('_custom:') ? value.replace('_custom:', '') : ''}
                    onChange={(e) => setValue(`_custom:${e.target.value}`)}
                    onClick={(e) => {
                      e.stopPropagation();
                      setValue('_custom:');
                    }}
                    className="flex-1 bg-transparent border-b border-neutral-300 dark:border-neutral-700 focus:border-neutral-700 dark:focus:border-neutral-350 px-1 py-0 text-[14px] font-normal text-neutral-850 dark:text-neutral-100 placeholder-neutral-400 outline-none"
                    disabled={disabled || isSubmitting}
                  />
                </label>
              )}
            </div>
          </div>
        );
      case 'TEXT_INPUT':
      default:
        return (
          <div className="flex flex-col gap-4 mt-5">
            <p className="text-[14.5px] font-normal text-neutral-900 dark:text-neutral-100">
              1. {intervention.question}
            </p>
            <form onSubmit={handleSubmit} className="flex gap-2">
              <input 
                type="text" 
                autoFocus
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Type your answer..."
                disabled={disabled || isSubmitting}
                className="flex-1 w-full rounded-lg bg-white/70 dark:bg-neutral-900 border border-neutral-250 dark:border-neutral-800 px-4 py-3 text-[14px] text-neutral-850 dark:text-neutral-100 placeholder-neutral-400 outline-none focus:border-neutral-700 dark:focus:border-neutral-300 transition-colors"
              />
            </form>
          </div>
        );
    }
  };

  return (
    <div 
      className={`my-6 bg-[#f0f4f9] dark:bg-[#1a1b1f] rounded-2xl overflow-hidden transition-all duration-300 font-sans ${
        disabled ? 'opacity-65' : ''
      }`}
      style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
    >
      {/* Header */}
      <div className="px-6 py-5 border-b border-neutral-200/50 dark:border-neutral-900/50">
        <div className="flex items-center gap-2 mb-2">
          <ChatIcon />
          <h4 className="text-[16px] font-medium text-neutral-900 dark:text-neutral-100 tracking-tight">
            {title}
          </h4>
        </div>
        <p className="text-[13.5px] font-normal text-neutral-500 dark:text-neutral-450 leading-relaxed">
          Your answers to these optional questions help provide better results.
        </p>
      </div>

      {/* Body */}
      <div className="px-6 py-5">
        {renderInput()}
        <div className="mt-7 flex justify-end">
          <button 
            onClick={() => handleSubmit()}
            disabled={!value || disabled || isSubmitting}
            className="px-6 py-2.5 rounded-full bg-neutral-900 dark:bg-white text-white dark:text-black hover:bg-neutral-850 dark:hover:bg-neutral-100 disabled:opacity-25 font-medium text-[13.5px] tracking-wide transition-all cursor-pointer active:scale-[0.98]"
          >
            {isSubmitting ? 'Resuming...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};
